import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuranContext = createContext();

export const useQuran = () => {
    const context = useContext(QuranContext);
    if (!context) {
        throw new Error('useQuran must be used within a QuranProvider');
    }
    return context;
};

export const QuranProvider = ({ children }) => {
    const { isAuthenticated, getAuthHeaders } = useAuth();
    
    // State
    const [surahs, setSurahs] = useState([]);
    const [currentSurah, setCurrentSurah] = useState(null);
    const [currentVerse, setCurrentVerse] = useState(1);
    const [verseData, setVerseData] = useState(null);
    const [translations, setTranslations] = useState([]);
    const [selectedTranslators, setSelectedTranslators] = useState(() => {
        try {
            const saved = localStorage.getItem('selectedTranslators');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            }
            // Fallback for previous string format
            const legacy = localStorage.getItem('selectedTranslator');
            if (legacy) return [legacy];
        } catch (e) {}
        return ['en.sahih'];
    });
    const [customTranslations, setCustomTranslations] = useState({});
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Root word browsing state
    const [rootBrowsingContext, setRootBrowsingContext] = useState(null);
    // { word: {...}, verses: [...], currentIndex: 0 }

    // Load surahs on mount
    useEffect(() => {
        const loadSurahs = async () => {
            try {
                const response = await axios.get(`${API}/surahs`);
                const chapters = response.data.chapters || [];
                setSurahs(chapters);
                
                // Check for URL parameters for deep linking
                const params = new URLSearchParams(window.location.search);
                const surahParam = params.get('surah');
                const verseParam = params.get('verse');
                
                if (surahParam && chapters.length > 0) {
                    const targetSurah = chapters.find(s => s.id === parseInt(surahParam, 10));
                    if (targetSurah) {
                        setCurrentSurah(targetSurah);
                        if (verseParam) {
                            setCurrentVerse(parseInt(verseParam, 10));
                        }
                        return;
                    }
                }
                
                // Set default surah to Al-Fatiha
                if (chapters.length > 0) {
                    setCurrentSurah(chapters[0]);
                }
            } catch (err) {
                console.error('Failed to load surahs:', err);
                setError('Failed to load surahs');
            }
        };
        loadSurahs();
    }, []);

    // Load topics when authenticated
    const loadTopics = useCallback(async () => {
        if (!isAuthenticated) {
            setTopics([]);
            return;
        }
        try {
            const response = await axios.get(`${API}/topics`, {
                headers: getAuthHeaders()
            });
            setTopics(response.data.topics || []);
        } catch (err) {
            console.error('Failed to load topics:', err);
        }
    }, [isAuthenticated, getAuthHeaders]);

    useEffect(() => {
        loadTopics();
    }, [loadTopics]);

    // Load translations list on mount
    useEffect(() => {
        const loadTranslations = async () => {
            try {
                const response = await axios.get(`${API}/translations`);
                setTranslations(response.data.translations || []);
            } catch (err) {
                console.error('Failed to load translations:', err);
            }
        };
        loadTranslations();
    }, []);

    // Save translator preference
    useEffect(() => {
        localStorage.setItem('selectedTranslators', JSON.stringify(selectedTranslators));
    }, [selectedTranslators]);

    // Load verse data when surah or verse changes
    const loadVerse = useCallback(async () => {
        if (!currentSurah) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const verseKey = `${currentSurah.id}:${currentVerse}`;
            const response = await axios.get(`${API}/verse/${verseKey}`, {
                params: { translation_ids: selectedTranslators.join(',') }
            });
            setVerseData(response.data.verse);
            
            // Load custom translation if user is authenticated
            if (isAuthenticated) {
                try {
                    const customResponse = await axios.get(`${API}/custom-translations/${verseKey}`, {
                        headers: getAuthHeaders()
                    });
                    if (customResponse.data.translation) {
                        setCustomTranslations(prev => ({
                            ...prev,
                            [verseKey]: {
                                text: customResponse.data.translation.translation_text,
                                reasoning: customResponse.data.translation.reasoning
                            }
                        }));
                    }
                } catch (err) {
                    // No custom translation exists, that's fine
                }
            }
        } catch (err) {
            console.error('Failed to load verse:', err);
            setError('Failed to load verse');
        } finally {
            setLoading(false);
        }
    }, [currentSurah, currentVerse, selectedTranslators, isAuthenticated, getAuthHeaders]);

    useEffect(() => {
        loadVerse();
    }, [loadVerse]);

    // Navigation functions
    const goToNextVerse = useCallback(() => {
        if (!currentSurah) return;
        
        if (currentVerse < currentSurah.verses_count) {
            setCurrentVerse(prev => prev + 1);
        } else {
            // Go to next surah
            const nextSurahIndex = surahs.findIndex(s => s.id === currentSurah.id) + 1;
            if (nextSurahIndex < surahs.length) {
                setCurrentSurah(surahs[nextSurahIndex]);
                setCurrentVerse(1);
            }
        }
    }, [currentSurah, currentVerse, surahs]);

    const goToPreviousVerse = useCallback(() => {
        if (!currentSurah) return;
        
        if (currentVerse > 1) {
            setCurrentVerse(prev => prev - 1);
        } else {
            // Go to previous surah
            const prevSurahIndex = surahs.findIndex(s => s.id === currentSurah.id) - 1;
            if (prevSurahIndex >= 0) {
                const prevSurah = surahs[prevSurahIndex];
                setCurrentSurah(prevSurah);
                setCurrentVerse(prevSurah.verses_count);
            }
        }
    }, [currentSurah, currentVerse, surahs]);

    const goToVerse = useCallback((surahId, verseNumber) => {
        const surah = surahs.find(s => s.id === surahId);
        if (surah) {
            setCurrentSurah(surah);
            setCurrentVerse(verseNumber);
        }
    }, [surahs]);

    // Root browsing navigation
    const startRootBrowsing = useCallback((word, verses, initialIndex = 0, browseType = 'root') => {
        setRootBrowsingContext({
            word,
            verses,
            currentIndex: initialIndex,
            browseType  // 'root' for root word browsing, 'search' for AI search
        });
    }, []);

    const goToRootVerse = useCallback((index) => {
        if (!rootBrowsingContext || !rootBrowsingContext.verses[index]) return;
        
        const verse = rootBrowsingContext.verses[index];
        const [surahId, verseNum] = verse.verse_key.split(':').map(Number);
        
        setRootBrowsingContext(prev => ({
            ...prev,
            currentIndex: index
        }));
        
        goToVerse(surahId, verseNum);
    }, [rootBrowsingContext, goToVerse]);

    const goToNextRootVerse = useCallback(() => {
        if (!rootBrowsingContext) return;
        const nextIndex = rootBrowsingContext.currentIndex + 1;
        if (nextIndex < rootBrowsingContext.verses.length) {
            goToRootVerse(nextIndex);
        }
    }, [rootBrowsingContext, goToRootVerse]);

    const goToPreviousRootVerse = useCallback(() => {
        if (!rootBrowsingContext) return;
        const prevIndex = rootBrowsingContext.currentIndex - 1;
        if (prevIndex >= 0) {
            goToRootVerse(prevIndex);
        }
    }, [rootBrowsingContext, goToRootVerse]);

    const clearRootBrowsing = useCallback(() => {
        setRootBrowsingContext(null);
    }, []);

    // Custom translation functions
    const saveCustomTranslation = useCallback(async (verseKey, text, reasoning = null) => {
        try {
            await axios.post(`${API}/custom-translations`, {
                verse_key: verseKey,
                translation_text: text,
                reasoning: reasoning
            }, {
                headers: getAuthHeaders()
            });
            setCustomTranslations(prev => ({
                ...prev,
                [verseKey]: { text, reasoning }
            }));
            return true;
        } catch (err) {
            console.error('Failed to save custom translation:', err);
            return false;
        }
    }, [getAuthHeaders]);

    const deleteCustomTranslation = useCallback(async (verseKey) => {
        try {
            await axios.delete(`${API}/custom-translations/${verseKey}`, {
                headers: getAuthHeaders()
            });
            setCustomTranslations(prev => {
                const newState = { ...prev };
                delete newState[verseKey];
                return newState;
            });
            return true;
        } catch (err) {
            console.error('Failed to delete custom translation:', err);
            return false;
        }
    }, [getAuthHeaders]);

    // Topics functions
    const createTopic = useCallback(async (name, description = '') => {
        try {
            const response = await axios.post(`${API}/topics`, 
                { name, description },
                { headers: getAuthHeaders() }
            );
            await loadTopics();
            return response.data;
        } catch (err) {
            console.error('Failed to create topic:', err);
            return null;
        }
    }, [getAuthHeaders, loadTopics]);

    const updateTopic = useCallback(async (topicId, name, description) => {
        try {
            const data = {};
            if (name !== undefined) data.name = name;
            if (description !== undefined) data.description = description;
            
            await axios.put(`${API}/topics/${topicId}`, data, {
                headers: getAuthHeaders()
            });
            await loadTopics();
            return true;
        } catch (err) {
            console.error('Failed to update topic:', err);
            return false;
        }
    }, [getAuthHeaders, loadTopics]);

    const deleteTopic = useCallback(async (topicId) => {
        try {
            await axios.delete(`${API}/topics/${topicId}`, {
                headers: getAuthHeaders()
            });
            await loadTopics();
            return true;
        } catch (err) {
            console.error('Failed to delete topic:', err);
            return false;
        }
    }, [getAuthHeaders, loadTopics]);

    const getTopicWithVerses = useCallback(async (topicId) => {
        try {
            const response = await axios.get(`${API}/topics/${topicId}`, {
                headers: getAuthHeaders()
            });
            return response.data; // { topic: {...}, verses: [...] }
        } catch (err) {
            console.error('Failed to get topic verses:', err);
            return null;
        }
    }, [getAuthHeaders]);

    const addVerseToTopic = useCallback(async (topicId, verseKey, comment = '') => {
        try {
            await axios.post(`${API}/topics/${topicId}/verses`, 
                { verse_key: verseKey, comment },
                { headers: getAuthHeaders() }
            );
            await loadTopics(); // Refresh counts
            return true;
        } catch (err) {
            console.error('Failed to add verse to topic:', err);
            return false;
        }
    }, [getAuthHeaders, loadTopics]);

    const removeVerseFromTopic = useCallback(async (topicId, verseId) => {
        try {
            await axios.delete(`${API}/topics/${topicId}/verses/${verseId}`, {
                headers: getAuthHeaders()
            });
            await loadTopics(); // Refresh counts
            return true;
        } catch (err) {
            console.error('Failed to remove verse from topic:', err);
            return false;
        }
    }, [getAuthHeaders, loadTopics]);

    const updateVerseComment = useCallback(async (topicId, verseId, comment) => {
        try {
            await axios.put(`${API}/topics/${topicId}/verses/${verseId}`, 
                { comment },
                { headers: getAuthHeaders() }
            );
            return true;
        } catch (err) {
            console.error('Failed to update verse comment:', err);
            return false;
        }
    }, [getAuthHeaders]);

    const reorderTopicVerses = useCallback(async (topicId, verseIds) => {
        try {
            await axios.put(`${API}/topics/${topicId}/reorder`, 
                { verse_ids: verseIds },
                { headers: getAuthHeaders() }
            );
            return true;
        } catch (err) {
            console.error('Failed to reorder topic verses:', err);
            return false;
        }
    }, [getAuthHeaders]);

    // Library functions
    const publishTopic = useCallback(async (topicId) => {
        try {
            const response = await axios.put(`${API}/topics/${topicId}/publish`, {}, {
                headers: getAuthHeaders()
            });
            await loadTopics();
            return response.data; // { status, is_public }
        } catch (err) {
            console.error('Failed to publish/unpublish topic:', err);
            return null;
        }
    }, [getAuthHeaders, loadTopics]);

    const getLibraryTopics = useCallback(async () => {
        try {
            const headers = isAuthenticated ? getAuthHeaders() : {};
            const response = await axios.get(`${API}/library`, { headers });
            return response.data.topics || [];
        } catch (err) {
            console.error('Failed to load library:', err);
            return [];
        }
    }, [isAuthenticated, getAuthHeaders]);

    const getPublicTopic = useCallback(async (topicId) => {
        try {
            const response = await axios.get(`${API}/library/${topicId}`);
            return response.data;
        } catch (err) {
            console.error('Failed to load public topic:', err);
            return null;
        }
    }, []);

    const clonePublicTopic = useCallback(async (topicId) => {
        try {
            const response = await axios.post(`${API}/library/${topicId}/clone`, {}, {
                headers: getAuthHeaders()
            });
            await loadTopics();
            return response.data;
        } catch (err) {
            console.error('Failed to clone topic:', err);
            return null;
        }
    }, [getAuthHeaders, loadTopics]);

    const value = {
        surahs,
        currentSurah,
        setCurrentSurah,
        currentVerse,
        setCurrentVerse,
        verseData,
        translations,
        selectedTranslators,
        setSelectedTranslators,
        customTranslations,
        saveCustomTranslation,
        deleteCustomTranslation,
        loading,
        error,
        goToNextVerse,
        goToPreviousVerse,
        goToVerse,
        loadVerse,
        // Root browsing
        rootBrowsingContext,
        startRootBrowsing,
        goToRootVerse,
        goToNextRootVerse,
        goToPreviousRootVerse,
        clearRootBrowsing,
        // Topics
        topics,
        loadTopics,
        createTopic,
        updateTopic,
        deleteTopic,
        getTopicWithVerses,
        addVerseToTopic,
        removeVerseFromTopic,
        updateVerseComment,
        reorderTopicVerses,
        // Library
        publishTopic,
        getLibraryTopics,
        getPublicTopic,
        clonePublicTopic
    };

    return (
        <QuranContext.Provider value={value}>
            {children}
        </QuranContext.Provider>
    );
};

export default QuranContext;
